import React, { useState, useEffect, useRef } from 'react'
import { productsApi, Product, ProductImage } from '../../services/api'

interface ProductManagerProps {
  onClose: () => void
}

const ALL_SIZES = ['4', '6', '8', '10', '12', '14', '16', 'S']
const ITEMS_PER_PAGE = 12

const normalizeProduct = (product: any): Product => ({
  ...product,
  available_sizes: Array.isArray(product.available_sizes)
    ? product.available_sizes
    : typeof product.available_sizes === 'string'
      ? JSON.parse(product.available_sizes)
      : Object.keys(product.available_sizes || {}),
  stock_by_size: typeof product.stock_by_size === 'string'
    ? JSON.parse(product.stock_by_size)
    : product.stock_by_size || {},
})

export const ProductManager: React.FC<ProductManagerProps> = ({ onClose }) => {
  // Lista
  const [products, setProducts]           = useState<Product[]>([])
  const [isLoading, setIsLoading]         = useState(true)
  const [error, setError]                 = useState<string | null>(null)

  // Paginación
  const [currentPage, setCurrentPage]     = useState(1)
  const [totalProducts, setTotalProducts] = useState(0)
  const totalPages = Math.ceil(totalProducts / ITEMS_PER_PAGE)

  // Formulario
  const [showForm, setShowForm]           = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)
  const [formError, setFormError]         = useState<string | null>(null)
  const [isSaving, setIsSaving]           = useState(false)
  const [uploadPct, setUploadPct]         = useState<number | null>(null)

  // Producto recién creado (para mostrar galería inmediatamente)
  const [justCreatedProduct, setJustCreatedProduct] = useState<Product | null>(null)

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    collection: 'all' as 'boys' | 'girls' | 'all',
    price_cop: 0,
    available_sizes: [] as string[],
    stock_by_size: {} as Record<string, number>,
    order_index: 0,
    image: null as File | null,
  })

  // Galería de imágenes adicionales
  const [extraImages, setExtraImages]     = useState<ProductImage[]>([])
  const [imgUploading, setImgUploading]   = useState(false)
  const [imgUploadPct, setImgUploadPct]   = useState<number | null>(null)
  const [imgError, setImgError]           = useState<string | null>(null)
  const extraImageRef = useRef<HTMLInputElement>(null)

  useEffect(() => { loadProducts(currentPage) }, [currentPage])

  const loadProducts = async (page: number) => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await productsApi.getAllAdmin(page, ITEMS_PER_PAGE)
      const raw = Array.isArray(response.data) ? response.data : []
      setProducts(raw.map(normalizeProduct))
      setTotalProducts(response.total ?? raw.length)
    } catch {
      setError('Error al cargar productos')
      setProducts([])
    } finally {
      setIsLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({ name: '', description: '', collection: 'all', price_cop: 0,
      available_sizes: [], stock_by_size: {}, order_index: 0, image: null })
    setFormError(null)
    setUploadPct(null)
    setExtraImages([])
    setImgError(null)
    setJustCreatedProduct(null)
  }

  const handleOpenForm = async () => {
    setEditingProduct(null)
    resetForm()
    // Auto-asignar orden consecutivo
    const nextOrder = await productsApi.getNextOrder()
    setFormData(prev => ({ ...prev, order_index: nextOrder }))
    setShowForm(true)
  }

  const handleEditClick = async (product: Product) => {
    setEditingProduct(product)
    setJustCreatedProduct(null)
    setFormData({
      name: product.name,
      description: product.description || '',
      collection: product.collection,
      price_cop: product.price_cop,
      available_sizes: product.available_sizes,
      stock_by_size: product.stock_by_size,
      order_index: product.order_index,
      image: null,
    })
    setImgError(null)
    setExtraImages([])
    setShowForm(true)
    try {
      const res = await productsApi.getImages(product.id)
      if (res.success && res.data) setExtraImages(res.data)
    } catch { /* sin imágenes aún */ }
  }

  const handleSizeToggle = (size: string) => {
    const newSizes = formData.available_sizes.includes(size)
      ? formData.available_sizes.filter(s => s !== size)
      : [...formData.available_sizes, size]
    const newStock = { ...formData.stock_by_size }
    if (!newSizes.includes(size)) delete newStock[size]
    else if (!(size in newStock)) newStock[size] = 0
    setFormData({ ...formData, available_sizes: newSizes, stock_by_size: newStock })
  }

  const handleStockChange = (size: string, quantity: number) => {
    setFormData({ ...formData, stock_by_size: { ...formData.stock_by_size, [size]: quantity } })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)
    if (!formData.name || !formData.price_cop) { setFormError('Nombre y precio son requeridos'); return }
    if (formData.available_sizes.length === 0) { setFormError('Selecciona al menos una talla'); return }

    try {
      setIsSaving(true)
      if (editingProduct) {
        const response = await productsApi.update(editingProduct.id, {
          name: formData.name,
          description: formData.description || undefined,
          collection: formData.collection,
          price_cop: formData.price_cop,
          available_sizes: formData.available_sizes,
          stock_by_size: formData.stock_by_size,
          order_index: formData.order_index,
          is_active: true,
        })
        if (response.success) {
          await loadProducts(currentPage)
          setShowForm(false)
          setEditingProduct(null)
          resetForm()
        } else {
          setFormError(response.error || 'Error al guardar. Si el error persiste, cierra sesión y vuelve a entrar.')
        }
      } else {
        // Crear producto
        const response = await productsApi.upload(
          formData.image || undefined,
          formData.name, formData.description, formData.collection,
          formData.price_cop, formData.available_sizes, formData.stock_by_size,
          formData.order_index,
          (pct) => setUploadPct(pct)
        )
        if (response.success && response.data) {
          await loadProducts(currentPage)
          // Mostrar galería para agregar más fotos al producto recién creado
          const created = normalizeProduct(response.data)
          setJustCreatedProduct(created)
          setEditingProduct(created)
          setUploadPct(null)
          // Limpiar solo los campos del form, mantener el modal abierto con la galería
          setFormData(prev => ({ ...prev, image: null }))
          setFormError(null)
          // Cargar imágenes reales del producto (la principal ya fue insertada en product_images)
          try {
            const imgRes = await productsApi.getImages(created.id)
            if (imgRes.success && imgRes.data) setExtraImages(imgRes.data)
          } catch { /* sin imágenes */ }
        }
      }
    } catch { setFormError('Error al guardar el producto') }
    finally { setIsSaving(false); setUploadPct(null) }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este producto?')) return
    try {
      setError(null)
      const response = await productsApi.delete(id)
      if (response.success) await loadProducts(currentPage)
    } catch { setError('Error al eliminar el producto') }
  }

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      setError(null)
      const response = await productsApi.update(id, { is_active: !isActive })
      if (response.success) await loadProducts(currentPage)
    } catch { setError('Error al actualizar el producto') }
  }

  // === Multi-imagen ===
  // Fuente única de verdad: product_images. Máximo 4 en total.
  const canAddMore = extraImages.length < 4
  const slotsLeft  = 4 - extraImages.length

  const handleExtraImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).slice(0, slotsLeft)
    const productId = editingProduct?.id || justCreatedProduct?.id
    if (!files.length || !productId) return
    if (!canAddMore) { setImgError('Máximo 4 imágenes por producto'); return }
    setImgError(null)
    setImgUploading(true)

    for (let i = 0; i < files.length; i++) {
      const file = files[i]!
      setImgUploadPct(0)
      try {
        const res = await productsApi.uploadExtraImage(productId, file, (pct) => {
          // Progreso global: pct de este archivo + archivos ya subidos
          setImgUploadPct(Math.round(((i * 100) + pct) / files.length))
        })
        if (res.success && res.data) setExtraImages(prev => [...prev, res.data!])
        else { setImgError(`Error al subir foto ${i + 1}`); break }
      } catch (err: any) { setImgError(err.message || `Error al subir foto ${i + 1}`); break }
    }

    setImgUploading(false)
    setImgUploadPct(null)
    if (extraImageRef.current) extraImageRef.current.value = ''
  }

  const handleDeleteExtraImage = async (imgId: string) => {
    const productId = editingProduct?.id || justCreatedProduct?.id
    if (!productId) return
    try {
      const deletedWasPrimary = extraImages.find(i => i.id === imgId)?.is_primary ?? false
      const res = await productsApi.deleteImage(productId, imgId)
      if (res.success) {
        setExtraImages(prev => {
          const remaining = prev.filter(i => i.id !== imgId)
          // Si era primaria, marcar la primera restante como primaria en UI
          if (deletedWasPrimary && remaining.length > 0) {
            return remaining.map((i, idx) => ({ ...i, is_primary: idx === 0 }))
          }
          return remaining
        })
      } else setImgError(res.error || 'Error al eliminar imagen')
    } catch { setImgError('Error al eliminar imagen') }
  }

  const handleSetPrimary = async (imgId: string) => {
    const productId = editingProduct?.id || justCreatedProduct?.id
    if (!productId) return
    try {
      const res = await productsApi.setPrimaryImage(productId, imgId)
      if (res.success) setExtraImages(prev => prev.map(i => ({ ...i, is_primary: i.id === imgId })))
      else setImgError(res.error || 'Error al marcar primaria')
    } catch { setImgError('Error al marcar imagen primaria') }
  }

  const collectionLabel = { boys: 'Niños', girls: 'Niñas', all: 'Unisex' }
  const getStockColor   = (q: number) => q >= 3 ? 'bg-green-100 text-green-800' : q >= 1 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
  const getStockEmoji   = (q: number) => q >= 3 ? '✅' : q >= 1 ? '⚠️' : '❌'

  // Sección galería: aparece cuando estamos editando O cuando recién creamos
  const galleryProductId = editingProduct?.id || justCreatedProduct?.id
  const showGallery      = Boolean(galleryProductId)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Gestión de Productos</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
        )}

        <div className="mb-6">
          <button
            onClick={handleOpenForm}
            className="bg-blue-500 hover:bg-blue-600 active:scale-95 text-white px-5 py-2.5 rounded-lg flex items-center gap-2 font-semibold shadow hover:shadow-md transition-all"
          >
            <span className="text-lg leading-none">+</span> Nuevo Producto
          </button>
        </div>

        {/* ========== MODAL FORMULARIO ========== */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-xl font-bold text-gray-800">
                  {justCreatedProduct
                    ? `✅ Creado: ${justCreatedProduct.name}`
                    : editingProduct
                      ? `Editar: ${editingProduct.name}`
                      : 'Nuevo Producto'}
                </h3>
                <button
                  onClick={() => { setShowForm(false); resetForm() }}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >×</button>
              </div>

              {/* Banner cuando recién se creó */}
              {justCreatedProduct && (
                <div className="bg-green-50 border border-green-300 text-green-800 px-4 py-3 rounded-lg mb-4 text-sm">
                  Producto creado correctamente. Ahora puedes agregar hasta 4 fotos.
                </div>
              )}

              {formError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">{formError}</div>
              )}

              {/* Formulario solo si NO está en modo "recién creado" */}
              {!justCreatedProduct && (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
                      <input type="text" value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Precio (COP)</label>
                      <input type="number" value={formData.price_cop}
                        onChange={(e) => setFormData({ ...formData, price_cop: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                    <textarea value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" rows={3} />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Colección</label>
                      <select value={formData.collection}
                        onChange={(e) => setFormData({ ...formData, collection: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="all">Unisex</option>
                        <option value="boys">Niños</option>
                        <option value="girls">Niñas</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Orden</label>
                      <input type="number" value={formData.order_index}
                        onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>

                  {/* Imagen principal — solo al crear; al editar se gestiona desde la galería */}
                  <div>
                    {!editingProduct && (
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Imagen principal (opcional)
                    </label>
                    )}
                    {editingProduct ? null : (
                      <>
                        <input type="file" accept="image/*"
                          onChange={(e) => setFormData({ ...formData, image: e.target.files?.[0] || null })}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                        <p className="text-xs text-gray-500 mt-1">Recomendado: 900×1200px (3:4). Máx 5MB.</p>
                        {uploadPct !== null && (
                          <div className="mt-2">
                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Subiendo imagen...</span><span>{uploadPct}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-blue-500 h-2 rounded-full transition-all duration-200" style={{ width: `${uploadPct}%` }} />
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Tallas */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tallas Disponibles</label>
                    <div className="flex flex-wrap gap-2">
                      {ALL_SIZES.map((size) => (
                        <label key={size} className="flex items-center gap-2">
                          <input type="checkbox" checked={formData.available_sizes.includes(size)}
                            onChange={() => handleSizeToggle(size)} className="w-4 h-4" />
                          <span className="text-sm">{size}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Stock */}
                  {formData.available_sizes.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Stock por Talla</label>
                      <div className="grid grid-cols-4 gap-2">
                        {formData.available_sizes.map((size) => (
                          <div key={size}>
                            <label className="text-xs text-gray-600">Talla {size}</label>
                            <input type="number" min="0" value={formData.stock_by_size[size] ?? 0}
                              onChange={(e) => handleStockChange(size, parseInt(e.target.value) || 0)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button type="submit" disabled={isSaving}
                      className="bg-green-500 hover:bg-green-600 active:scale-95 disabled:bg-gray-400 text-white px-6 py-2.5 rounded-lg font-semibold shadow hover:shadow-md transition-all">
                      {isSaving ? 'Guardando...' : editingProduct ? 'Actualizar' : 'Crear Producto'}
                    </button>
                    <button type="button" onClick={() => { setShowForm(false); resetForm() }}
                      className="bg-gray-200 hover:bg-gray-300 active:scale-95 text-gray-700 px-6 py-2.5 rounded-lg font-semibold transition-all">
                      Cancelar
                    </button>
                  </div>
                </form>
              )}

              {/* ===== GALERÍA DE IMÁGENES (editar o recién creado) ===== */}
              {showGallery && (
                <div className={`${!justCreatedProduct ? 'border-t mt-4 pt-4' : ''}`}>
                  {/* Galería unificada — fuente única: product_images */}
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700">
                      Fotos del producto ({extraImages.length}/4)
                    </label>
                    {canAddMore && (
                      <button type="button"
                        onClick={() => extraImageRef.current?.click()}
                        disabled={imgUploading}
                        className="text-xs bg-gray-100 hover:bg-gray-200 active:scale-95 text-gray-700 px-3 py-1.5 rounded-lg font-medium transition-all disabled:opacity-50">
                        {imgUploading
                          ? `Subiendo... ${imgUploadPct ?? 0}%`
                          : slotsLeft === 1
                            ? '+ Agregar 1 foto'
                            : `+ Agregar hasta ${slotsLeft} fotos`}
                      </button>
                    )}
                    <input ref={extraImageRef} type="file" accept="image/jpeg,image/png,image/webp"
                      multiple className="hidden" onChange={handleExtraImageUpload} />
                  </div>

                  {/* Barra progreso extra */}
                  {imgUploadPct !== null && (
                    <div className="mb-3">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full transition-all duration-200" style={{ width: `${imgUploadPct}%` }} />
                      </div>
                      <p className="text-xs text-gray-500 mt-1 text-right">{imgUploadPct}%</p>
                    </div>
                  )}

                  {imgError && <p className="text-xs text-red-600 mb-2">{imgError}</p>}

                  {extraImages.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">Sin fotos aún. Agrega hasta 4.</p>
                  ) : (
                    <div className="flex gap-3 flex-wrap">
                      {extraImages.map((img) => (
                        <div key={img.id} className="relative group">
                          <img src={img.full_url} alt=""
                            className={`w-20 h-24 object-cover rounded-lg border-2 transition-all ${img.is_primary ? 'border-blue-500' : 'border-gray-200'}`} />
                          {img.is_primary && (
                            <span className="absolute top-1 left-1 bg-blue-500 text-white text-[9px] px-1 rounded font-bold">Principal</span>
                          )}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 rounded-lg transition-all flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                            {/* ★ solo en no-primarias */}
                            {!img.is_primary && (
                              <button type="button" onClick={() => handleSetPrimary(img.id)}
                                title="Marcar como principal"
                                className="bg-blue-500 text-white text-xs px-1.5 py-1 rounded font-bold">★</button>
                            )}
                            {/* ✕ en cualquier imagen si hay más de 1 */}
                            {extraImages.length > 1 && (
                              <button type="button" onClick={() => handleDeleteExtraImage(img.id)}
                                title="Eliminar imagen"
                                className="bg-red-500 text-white text-xs px-1.5 py-1 rounded font-bold">✕</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mt-2">★ = marcar como principal &nbsp; ✕ = eliminar &nbsp; (pasa el mouse sobre la imagen)</p>

                  {/* Botón cerrar cuando recién se creó */}
                  {justCreatedProduct && (
                    <div className="mt-5">
                      <button
                        onClick={() => { setShowForm(false); resetForm() }}
                        className="bg-blue-500 hover:bg-blue-600 active:scale-95 text-white px-6 py-2.5 rounded-lg font-semibold shadow hover:shadow-md transition-all">
                        Listo, cerrar
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========== LISTA DE PRODUCTOS ========== */}
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-2 text-gray-600">Cargando productos...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {products.map((product) => (
              <div key={product.id}
                className="border border-gray-200 rounded-lg p-4 flex gap-4 items-start hover:border-blue-300 hover:bg-blue-50 transition-colors">

                <div className="flex-shrink-0">
                  {product.full_image_url || product.image_url ? (
                    <img src={product.full_image_url || product.image_url || ''}
                      alt={product.name} className="w-16 h-20 object-cover rounded"
                      onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/64x80/CCCCCC/666666?text=Sin+imagen' }} />
                  ) : (
                    <div className="w-16 h-20 bg-gray-200 rounded flex items-center justify-center">
                      <span className="text-gray-400 text-xs">Sin</span>
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800 text-sm">{product.name}</h3>
                      <p className="text-lg font-bold text-green-600">${product.price_cop.toLocaleString('es-CO')}</p>
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap ml-2">Ord. {product.order_index}</span>
                  </div>
                  {product.description && (
                    <p className="text-xs text-gray-600 line-clamp-2 mb-2">{product.description}</p>
                  )}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full">
                      {collectionLabel[product.collection]}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${product.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {product.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                    {/* Indicador de cuántas fotos tiene */}
                    {(product.extra_images?.length ?? 0) > 0 && (
                      <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">
                        📷 {product.extra_images?.length ?? 0} fotos
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex-shrink-0">
                  {product.available_sizes?.length > 0 ? (
                    <div className="border border-gray-300 rounded overflow-hidden bg-white shadow-sm">
                      <table className="text-xs">
                        <tbody>
                          {product.available_sizes.map((size, idx) => {
                            const stock = product.stock_by_size?.[size] ?? 0
                            return (
                              <tr key={size}
                                className={`${idx < product.available_sizes.length - 1 ? 'border-b border-gray-200' : ''} ${getStockColor(stock)}`}>
                                <td className="px-2 py-1.5 font-semibold">T{size}</td>
                                <td className="px-2 py-1.5 border-l border-gray-200 text-center font-bold whitespace-nowrap">
                                  {stock} {getStockEmoji(stock)}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-500">Sin tallas</span>
                  )}
                </div>

                <div className="flex-shrink-0 flex gap-2.5 flex-col">
                  <button onClick={() => handleToggleActive(product.id, product.is_active)}
                    className={`px-4 py-2 rounded-lg whitespace-nowrap font-semibold text-sm shadow hover:shadow-md active:scale-95 transition-all ${product.is_active ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`}>
                    {product.is_active ? 'Desactivar' : 'Activar'}
                  </button>
                  <button onClick={() => handleEditClick(product)}
                    className="bg-blue-500 hover:bg-blue-600 active:scale-95 text-white px-4 py-2 rounded-lg text-sm whitespace-nowrap font-semibold shadow hover:shadow-md transition-all">
                    Editar
                  </button>
                  <button onClick={() => handleDelete(product.id)}
                    className="bg-red-500 hover:bg-red-600 active:scale-95 text-white px-4 py-2 rounded-lg text-sm whitespace-nowrap font-semibold shadow hover:shadow-md transition-all">
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && products.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>No hay productos aún.</p>
            <p className="text-sm">Crea el primer producto usando el botón de arriba.</p>
          </div>
        )}

        {/* ========== PAGINACIÓN ========== */}
        {!isLoading && totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-lg font-semibold text-sm bg-gray-100 hover:bg-gray-200 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
              ← Anterior
            </button>
            <span className="text-sm text-gray-600">
              Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong>
              <span className="text-gray-400 ml-2">({totalProducts} productos)</span>
            </span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-lg font-semibold text-sm bg-gray-100 hover:bg-gray-200 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
              Siguiente →
            </button>
          </div>
        )}

      </div>
    </div>
  )
}
